import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { pricingTable, PHONE } from "@/mocks/updiag";

const packs = [
  {
    name: "Pack Vente",
    subtitle: "Dossier de Diagnostic Technique complet",
    price: "Dès 280 €",
    color: "border-[#0F3B5F]",
    badge: null,
    items: ["DPE", "Diagnostic Amiante", "Plomb (CREP)", "Diagnostic Électricité", "Diagnostic Gaz", "Loi Carrez (si copropriété)", "État des risques (ERP)"],
    cta: "/diagnostics/pack-vente",
  },
  {
    name: "Pack Location",
    subtitle: "Diagnostics obligatoires pour louer",
    price: "Dès 180 €",
    color: "border-[#F97316]",
    badge: "Populaire",
    items: ["DPE", "Plomb (CREP)", "Diagnostic Électricité", "Diagnostic Gaz", "État des risques (ERP)"],
    cta: "/diagnostics/pack-location",
  },
  {
    name: "DPE Seul",
    subtitle: "Diagnostic de Performance Énergétique",
    price: "Dès 130 €",
    color: "border-gray-200",
    badge: null,
    items: ["Évaluation énergétique complète", "Étiquette énergie A→G", "Recommandations travaux", "Rapport certifié COFRAC", "Validité 10 ans"],
    cta: "/diagnostics/dpe",
  },
];

export default function TarifsPage() {
  return (
    <>
      <title>Tarifs Diagnostics Immobiliers Paris & IDF — Dès 80 € | Up Diag</title>
      <meta name="description" content="Tarifs transparents pour tous vos diagnostics immobiliers à Paris et en Île-de-France. DPE dès 130 €, Pack Vente dès 280 €. Devis gratuit en 2 minutes." />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F3B5F] to-[#0a2d4a] py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/10 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Tarifs transparents
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
              Nos tarifs de diagnostic<br />
              <span className="text-[#F97316]">affichés sans surprise</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Tous nos prix sont TTC, déplacement inclus en Île-de-France. Obtenez votre devis personnalisé gratuit en 2 minutes.
            </p>
          </div>
        </section>

        {/* Packs */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>Nos packs les plus demandés</h2>
              <p className="text-gray-500">Regroupez vos diagnostics en un seul rendez-vous et économisez.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packs.map((pack) => (
                <div key={pack.name} className={`bg-white rounded-2xl p-8 border-2 ${pack.color} relative hover:-translate-y-1 transition-all duration-300`}>
                  {pack.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F97316] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      {pack.badge}
                    </span>
                  )}
                  <h3 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{pack.name}</h3>
                  <p className="text-sm text-gray-400 mb-5">{pack.subtitle}</p>
                  <div className="text-3xl font-extrabold text-[#0F3B5F] mb-6" style={{ fontFamily: "Barlow, sans-serif" }}>{pack.price}</div>
                  <ul className="flex flex-col gap-2.5 mb-7">
                    {pack.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#16A34A]"></i>{item}
                      </li>
                    ))}
                  </ul>
                  <Link to={pack.cta} className="block w-full text-center py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                    En savoir plus
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tableau détaillé */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>Tarif détaillé par diagnostic</h2>
              <p className="text-gray-500">Prix TTC, déplacement inclus en Île-de-France.</p>
            </div>
            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0F3B5F] text-white">
                    <th className="text-left px-6 py-4 text-sm font-bold" style={{ fontFamily: "Barlow, sans-serif" }}>Service</th>
                    <th className="text-left px-6 py-4 text-sm font-bold hidden md:table-cell">Description</th>
                    <th className="text-right px-6 py-4 text-sm font-bold">Prix TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTable.map((row, i) => (
                    <tr key={i} className={`border-b border-gray-50 hover:bg-orange-50/40 transition-colors ${row.highlight ? "border-l-4 border-l-[#F97316]" : ""} ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-bold ${row.highlight ? "text-[#F97316]" : "text-[#0F3B5F]"}`}>{row.service}</span>
                        {row.highlight && <span className="ml-2 bg-orange-100 text-[#F97316] text-[10px] font-bold px-2 py-0.5 rounded-full">★ Phare</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">{row.description}</td>
                      <td className="px-6 py-4 text-right text-lg font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>{row.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">* Tarifs indicatifs TTC. Devis personnalisé gratuit selon surface, type de bien et commune.</p>
          </div>
        </section>

        {/* FAQ Tarifs */}
        <section className="bg-[#F8FAFC] py-16">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-8 text-center" style={{ fontFamily: "Barlow, sans-serif" }}>Questions sur nos tarifs</h2>
            <div className="flex flex-col gap-4">
              {[
                { q: "Le déplacement est-il inclus dans le tarif ?", a: "Oui, le déplacement est inclus dans tous nos tarifs pour les interventions en Île-de-France (75, 92, 93, 94, 78, 91, 77, 95)." },
                { q: "Y a-t-il des frais supplémentaires ?", a: "Non. Le tarif affiché est le prix final TTC. Aucun frais caché, aucune surprise à la facturation." },
                { q: "Puis-je payer en plusieurs fois ?", a: "Oui, nous proposons le paiement en 3 fois sans frais pour les montants supérieurs à 200 €. Contactez-nous pour en savoir plus." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-[#0F3B5F] mb-2 text-sm">{item.q}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>Obtenez votre devis personnalisé</h2>
            <p className="text-white/75 mb-8">Gratuit, sans engagement, réponse sous 2h.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" className="flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                <i className="ri-file-list-3-line"></i> Devis gratuit en 2 min
              </Link>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
